import { Button, Switch, Tooltip, Box, styled } from '@mui/material';
import TextField from 'components/FormElements/AppTextInput';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import FormHelperText from '@mui/material/FormHelperText';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { makeStyles } from 'tss-react/mui';
import FieldRow from 'components/FormElements/FieldRow';
import { TrancheVisibility } from 'further-types/fund';
import { FirmSelector } from 'components/FirmSelector';
import ButtonGroup from 'components/ButtonGroup';
import RecordView from 'components/RecordView';
import { FundStatus } from 'adminConstants/fundProperties';
import { useFirm } from 'hooks/data/firm/useFirm';
import { NumericFormat } from 'react-number-format';

const ToggleContainer = styled(Box)(() => ({
  width: 'fit-content',
}));

const useStyles = makeStyles()((theme) => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  switchLabel: {
    margin: '0 10px',

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  tooltipPadding: {
    paddingLeft: 20,
  },
  tooltipLink: {
    color: theme.palette.text.rootColor,
  },
}));

type Props = any;

const BasicInformation: React.FC<Props> = ({
  errorTextBasic,
  handleCancel,
  handleChangeTag,
  handleClick,
  handleDateChange,
  id,
  investment,
  setValue,
  tagList,
  value,
  disabled = false,
  hasPrerequisitesForPublishing,
  isSyndicate,
  viewOnly = false,
}) => {
  const { classes } = useStyles();

  const { firms } = useFirm({ params: { firmId: value.firmId } });

  const permittedClassifications =
    firms?.data?.result?.[0]?.whiteLabelConfig?.permittedClassifications;

  const allowProfessionalClients = permittedClassifications?.professional;

  const isDraft = value.fundStatus === FundStatus.DRAFT;
  const isOpen = value.fundStatus === FundStatus.OPEN;

  return (
    <RecordView>
      <FieldRow title="Firm*" centerTitle>
        <FirmSelector
          firmId={value.firmId}
          onChange={(firmId) => setValue({ ...value, firmId })}
          placeholder="Select firm"
          allowDeselection={false}
          error={errorTextBasic.firmId}
          helperText={errorTextBasic.firmId}
          required
          disabled={viewOnly}
        />
      </FieldRow>
      <FieldRow title="Tranche name*" centerTitle>
        <TextField
          required
          name="tancheName"
          disabled={viewOnly}
          fullWidth
          onChange={(event) =>
            setValue({ ...value, fundName: event.target.value })
          }
          value={value.fundName}
          placeholder="Enter the tranche name"
          variant="outlined"
          error={errorTextBasic.fundName}
          helperText={errorTextBasic.fundName}
        />
      </FieldRow>
      <FieldRow
        title="Tranche status*"
        centerTitle
        tooltipText={
          !hasPrerequisitesForPublishing && !isOpen
            ? `To publish this tranche, please first select your payment account in “Bank details”${
                !isSyndicate
                  ? ' and add your Legal agreement in “Tranche subscription documentation”.'
                  : '.'
              }`
            : ''
        }
      >
        <AppRadioButton
          required
          key="open"
          name="fundStatus"
          label="Open"
          value={1}
          checked={value.fundStatus === 1}
          onChange={() => setValue({ ...value, fundStatus: 1 })}
          error={errorTextBasic.fundStatus}
          helperText={errorTextBasic.fundStatus}
          disabled={(!hasPrerequisitesForPublishing && !isOpen) || viewOnly}
        />
        <AppRadioButton
          key="closed"
          name="fundStatus"
          label="Closed"
          value={0}
          checked={value.fundStatus === 0}
          onChange={() => setValue({ ...value, fundStatus: 0 })}
          disabled={viewOnly}
        />
        {!investment.length && (
          <AppRadioButton
            key="draft"
            name="fundStatus"
            label="Draft"
            value={2}
            checked={isDraft}
            onChange={() => setValue({ ...value, fundStatus: 2 })}
            error={errorTextBasic.fundStatus}
            helperText={errorTextBasic.fundStatus}
            disabled={viewOnly}
          />
        )}
      </FieldRow>

      <FieldRow
        title="Show on firm link?"
        centerTitle
        tooltipText={
          <>
            If you wish to hide this tranche from your portal investment flow
            (so it is only visible for investors with this tranche’s specific
            investment link), toggle this setting to{' '}
            <i>Only show on tranche link</i>.
          </>
        }
      >
        <span className={classes.switchLabel}>Only show on tranche link</span>
        <Switch
          key="trancheVisibility"
          disabled={viewOnly}
          checked={value.trancheVisibility === TrancheVisibility.ShowOnFirm}
          onChange={(event) =>
            setValue({
              ...value,
              trancheVisibility: event.target.checked
                ? TrancheVisibility.ShowOnFirm
                : TrancheVisibility.ShowOnTranche,
            })
          }
        />
        <span className={classes.switchLabel}>Show on firm link</span>
      </FieldRow>

      <FieldRow
        title="Fund type*"
        centerTitle
        tooltipText={
          <div>
            <p>
              Select the type of investment(s) being made with this tranche:
            </p>
            <br />
            <ul className={classes.tooltipPadding}>
              <li>
                For mixed EIS and SEIS funds, select <b>EIS</b>.
              </li>
              <li>
                For direct investments <i>with tax relief</i> that you want to
                display individually on client portals, select <b>EIS</b> or{' '}
                <b>SEIS</b> (as applicable).
              </li>
              <li>
                For direct investments <i>with no tax relief</i> that you want
                to display individually on client portals, select <b>Direct</b>.
              </li>
              <li>
                For direct investments <i>with or without tax relief</i> you
                want to display grouped: (i) by tax year, (ii) by calendar year
                or (iii) all as one on client portals (setting adjustable in
                your{' '}
                <a
                  className={classes.tooltipLink}
                  href="/dashboard/my-firm"
                  target="_blank"
                  rel="noreferrer"
                >
                  My Firm
                </a>{' '}
                page), select <b>Syndicate</b>.
              </li>
            </ul>
          </div>
        }
        tooltipMaxWidth={600}
      >
        {tagList.map((item, index) => (
          <AppRadioButton
            key={index}
            name="tag"
            label={item.name}
            value={item._id}
            checked={value.tag === item?._id}
            onChange={(event) => handleChangeTag(event.target.value, item)}
            disabled={viewOnly}
          />
        ))}
        {errorTextBasic.tag && (
          <FormHelperText error={true}>{errorTextBasic.tag}</FormHelperText>
        )}
      </FieldRow>

      <FieldRow title="Professional clients only" centerTitle>
        <Tooltip
          title={
            !allowProfessionalClients ? (
              <>
                You cannot make a tranche professional-only as your firm has
                professional investor classification switched off. To turn it
                on, change the setting in the <i>Portal and KYC</i> section of
                the <i>My Firm</i> page first.
              </>
            ) : (
              ''
            )
          }
        >
          <ToggleContainer>
            <span className={classes.switchLabel}>No</span>
            <Switch
              disabled={!allowProfessionalClients || viewOnly}
              key="isProfessionalClientsOnly"
              checked={value.isProfessionalClientsOnly}
              onChange={(event) =>
                setValue({
                  ...value,
                  isProfessionalClientsOnly: event.target.checked,
                })
              }
            />
            <span className={classes.switchLabel}>Yes</span>
          </ToggleContainer>
        </Tooltip>
      </FieldRow>

      <FieldRow
        title="Fund raise limit"
        centerTitle
        tooltipText="Set an amount at which the tranche will be automatically closed."
      >
        <NumericFormat
          name="fundRaiseLimit"
          prefix="£"
          decimalScale={2}
          fixedDecimalScale
          customInput={TextField}
          allowNegative={false}
          allowLeadingZeros={false}
          thousandSeparator
          placeholder="Enter fund raise limit"
          value={value.fundRaiseLimit ?? ''}
          onValueChange={({ value: nextValue }) =>
            setValue({
              ...value,
              fundRaiseLimit: nextValue ? Number(nextValue) : null,
            })
          }
        />
      </FieldRow>

      <FieldRow title="Start date" centerTitle>
        <AppDatePicker
          name="startDate"
          value={value.startDate}
          disabled={viewOnly}
          onChange={(date) => {
            setValue({ ...value, startDate: date });
          }}
        />
      </FieldRow>

      <FieldRow title="Scheduled close date*" centerTitle>
        <AppDatePicker
          required
          name="finishDate"
          value={value.finishDate}
          disabled={viewOnly}
          onChange={handleDateChange}
          error={errorTextBasic.finishDate}
          helperText={errorTextBasic.finishDate}
        />
      </FieldRow>
      {id && (
        <FieldRow title="Close date" centerTitle>
          <AppDatePicker
            name="closedDate"
            value={value.closedDate}
            onChange={(date) => {
              setValue({ ...value, closedDate: date });
            }}
            disabled={viewOnly}
          />
        </FieldRow>
      )}

      {!viewOnly && (
        <ButtonGroup>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            className={classes.marginLeftBtn}
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={() => handleClick()}
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </ButtonGroup>
      )}
    </RecordView>
  );
};

export default BasicInformation;
